import { ToastApp, ToastAppProps } from '../view/containers/ToastApp';
import { appendComponentInWindow, renderComponentInWindow } from '../view/utils/RenderApp';
import { ServiceStore } from '../store/ServiceStore';
import { LayoutableWindow, LayoutableWindowState, Point } from '../controller/LayoutableWindow';
import { clamp } from '../utils';
import { WindowManager } from '../controller/WindowManager';
import { TOAST_WINDOW_NAME } from '../constants';

import { StoredNotification } from './StoredNotification';

enum RenderMode {
  MEASURE,
  RENDER,
  UPDATE,
}

export class Toast extends LayoutableWindow {
  protected readonly _maxSize: Point = { x: 312, y: -1 };
  private _notification: StoredNotification;
  private _renderTarget?: HTMLDivElement;
  /**
   * The Notification associated with this Toast
   */
  public get notification(): StoredNotification {
    return this._notification;
  }

  public set notification(value: StoredNotification) {
    // Don't allow sticky state to change on toast
    if (value.notification.sticky !== this.notification.notification.sticky) {
      throw new Error(
        'Changing a toast to a notification that has a different `sticky` state is not allowed.'
      );
    }
    this._notification = value;
    this._sizeModifier = { x: 0, y: 0 };
    this.renderComponent(RenderMode.UPDATE);
  }

  /**
   * The id of the notification this Toast represents.
   */
  public get id(): string {
    return this.notification.id;
  }

  /**
   * The openfin window name for this toast
   */
  public get windowName(): string {
    return this.isSticky ? `${TOAST_WINDOW_NAME}:sticky` : `${TOAST_WINDOW_NAME}:${this.id}`;
  }

  /**
   * Flag used to identify sticky toasts.
   *
   * Sticky toasts do not close when `maxTimeout` expires.
   */
  public get isSticky(): boolean {
    return this.notification.notification.sticky === 'sticky';
  }

  public constructor(
    store: ServiceStore,
    windowManager: WindowManager,
    notification: StoredNotification
  ) {
    super(store, windowManager, notification.id);
    this._notification = notification;
    this._renderTarget = undefined;
    this.init();
  }

  private async init(): Promise<void> {
    await this.renderComponent(RenderMode.MEASURE);
  }

  protected async postWindowGrab(): Promise<void> {
    await this.renderComponent(RenderMode.RENDER);
  }

  private async resizeWindow(size: Point): Promise<void> {
    this.updateSize(size);
    this.windowManager.relayout();
  }

  private async adjustSizeWindow(amount: Point): Promise<void> {
    this._sizeModifier = amount;
    this.windowManager.relayout();
  }

  private updateSize(size: Point, removeModifiers = true): void {
    const xMod = removeModifiers ? this._sizeModifier.x : 0;
    const yMod = removeModifiers ? this._sizeModifier.y : 0;

    this._size = {
      x: clamp(size.x - xMod, this.maxSize.x, this.maxSize.x) | 0,
      y: (size.y - yMod) | 0,
    };
  }

  private async renderComponent(renderMode: RenderMode) {
    const { store } = this;
    const measuring = renderMode === RenderMode.MEASURE;
    const window = measuring ? await this.windowManager.measurementWindow() : await this.webWindow;
    let postRender: (size: Point) => void;
    switch (renderMode) {
      case RenderMode.MEASURE:
        postRender = (size: Point) => {
          this.updateSize(size);
          // Now we have the size, we can remove the node from measurement window if it still exists.
          if (this._renderTarget) {
            this._renderTarget.remove();
            this._renderTarget = undefined;
          }
          this.setState(LayoutableWindowState.QUEUED);
        };
        break;
      case RenderMode.RENDER:
        postRender = () => {
          this.setState(LayoutableWindowState.RENDERED);
        };
        break;
      case RenderMode.UPDATE:
        postRender = (size: Point) => {
          this.updateSize(size);
        };
        break;
    }

    const props: ToastAppProps = {
      notificationId: this.notification.id,
      setWindowSize: postRender,
    };

    // Wait for the React component to render and then get the dimensions of it to resize the window.
    try {
      if (measuring) {
        this._renderTarget = appendComponentInWindow(ToastApp, window, store, props);
      } else {
        props.onResize = this.resizeWindow.bind(this);
        props.onModifySize = this.adjustSizeWindow.bind(this);
        const domTarget = window.document.getElementById('react-app');
        if (!domTarget) {
          throw new Error(`Unable to get toast window dom target ${this.id}`);
        }
        renderComponentInWindow(ToastApp, window, store, domTarget, props);
      }
    } catch (error) {
      console.error(`Toast failed to render. Error was ${error}`);
      this.setState(LayoutableWindowState.CLOSED);
    }
  }
}
